import chatProxyWsConnection from './chatProxyWsConnection';
import { SupportedChatChannel } from '../supportedChatChannel';
import { ChatConfig, ChatListener, ChatListenerCallbacks } from '../interface';
import { ChatWsMessageType } from './chatWsWrapper';
import {
  ChatProxyTargetCreated,
  ChatProxyWsEvent,
  ChatProxyWsIncomingMessage,
  ChatProxyWsOutgoingMessage,
} from './chatProxyWsMessage';
import { ChatAttachment, ChatMessagePayloadType, ChatMessageStatus } from '../../types/chatMessage';
import dateTimeService from '../../service/dateTimeService';
import phoneNumberService from '../../service/phoneNumberService';
import { ChatClientType } from '../../types/chatClient';
import { getMediaIdFromUrl, buildGtwWhatsAppMediaUrl } from '../chatMediaUrl';

export abstract class AbstractChatProxyListener implements ChatListener {
  private registeredConnectionListener: string | undefined;
  private registeredMessageListeners: string[] = [];

  protected constructor(private readonly channel: SupportedChatChannel,
                        private readonly config: ChatConfig) {}

  register(callbacks: ChatListenerCallbacks): void {
    this.registeredConnectionListener = chatProxyWsConnection.addConnectionListener({
      onOpen: () => callbacks.onConnect?.(this.channel),
      onClose: () => callbacks.onDisconnect?.(this.channel),
    });

    this.registeredMessageListeners = [
      chatProxyWsConnection.addMessageListener({
        channel: this.channel,
        msgType: ChatWsMessageType.EVENT,
        onWsMessage: wsMessage => this.onChatEvent(wsMessage, callbacks),
      }),
      chatProxyWsConnection.addMessageListener({
        channel: this.channel,
        msgType: ChatWsMessageType.INCOMING_MESSAGE,
        onWsMessage: wsMessage => this.onChatIncomingMessage(wsMessage, callbacks),
      }),
      chatProxyWsConnection.addMessageListener({
        channel: this.channel,
        msgType: ChatWsMessageType.OUTGOING_MESSAGE,
        onWsMessage: wsMessage => this.onChatOutgoingMessage(wsMessage, callbacks),
      }),
      chatProxyWsConnection.addMessageListener({
        channel: this.channel,
        msgType: ChatWsMessageType.TARGET_CREATED,
        onWsMessage: wsMessage => this.onChatTargetCreated(wsMessage, callbacks),
      }),
    ];
  }

  unregister(): void {
    this.registeredMessageListeners.forEach(listenerId => {
      chatProxyWsConnection.removeMessageListener(listenerId);
    });

    if (this.registeredConnectionListener) {
      chatProxyWsConnection.removeConnectionListener(this.registeredConnectionListener);
    }
  }

  private onChatEvent(wsMessage: ChatProxyWsEvent, callbacks: ChatListenerCallbacks): void {
    const { payload } = wsMessage;

    callbacks.onEvent?.({
      channel: this.channel,
      msgId: payload.msgId,
      status: payload.status,
      statusDetails: payload.details,
      timestamp: payload.timestamp,
    });
  }

  private onChatIncomingMessage(wsMessage: ChatProxyWsIncomingMessage, callbacks: ChatListenerCallbacks): void {
    const { payload: msg } = wsMessage;
    const rawPayload = msg.payload as any;
    const timestamp = typeof rawPayload?.timestamp === 'number'
      ? Math.floor(rawPayload.timestamp / 1000)
      : dateTimeService.getCurrentTimestamp();

    const attachments: ChatAttachment[] = [];
    const payloadAttachments = Array.isArray(rawPayload?.attachments) ? rawPayload.attachments : [];
    payloadAttachments.forEach((att: any) => {
      if (!att?.mediaUrl) return;
      const mediaId = getMediaIdFromUrl(att.mediaUrl);
      if (mediaId && attachments.some((a) => getMediaIdFromUrl(a.link) === mediaId)) return;
      attachments.push({
        link: att.mediaUrl,
        filename: att.filename,
        caption: att.caption,
      });
    });

    // Some chat-proxy payloads provide single attachment fields instead of payload.attachments[].
    const singleAttachmentLink =
      rawPayload?.attachmentLink ||
      rawPayload?.mediaUrl ||
      rawPayload?.link ||
      rawPayload?.url ||
      rawPayload?.document?.mediaUrl ||
      rawPayload?.document?.link ||
      rawPayload?.attachment?.mediaUrl ||
      rawPayload?.attachment?.link ||
      (msg as any).attachmentLink ||
      (msg as any).mediaUrl ||
      (msg as any).link ||
      (msg as any).url;
    const singleAttachmentFilename =
      rawPayload?.fileName ||
      rawPayload?.filename ||
      rawPayload?.name ||
      rawPayload?.document?.fileName ||
      rawPayload?.document?.filename ||
      rawPayload?.attachment?.fileName ||
      rawPayload?.attachment?.filename ||
      (msg as any).fileName ||
      (msg as any).filename ||
      (msg as any).name;
    const payloadType = this.normalizePayloadType(
      rawPayload?.type || rawPayload?.payloadType || (msg as any)?.payloadType || (msg as any)?.type
    );
    const singleAttachmentCaption =
      rawPayload?.caption || rawPayload?.text || rawPayload?.message || (msg as any).message;
    const mediaMessageId = msg.uuid || (msg as any).messageId;
    const isMediaPayloadType =
      payloadType === ChatMessagePayloadType.IMAGE ||
      payloadType === ChatMessagePayloadType.VIDEO ||
      payloadType === ChatMessagePayloadType.DOC ||
      payloadType === ChatMessagePayloadType.IMAGE_BUTTON;
    const mediaIdFallbackLink =
      this.channel === SupportedChatChannel.WHATSAPP &&
      isMediaPayloadType &&
      mediaMessageId
        ? buildGtwWhatsAppMediaUrl(mediaMessageId)
        : undefined;
    const resolvedAttachmentLink = singleAttachmentLink || mediaIdFallbackLink;
    if (resolvedAttachmentLink) {
      const resolvedMediaId = getMediaIdFromUrl(resolvedAttachmentLink);
      const sameMediaIndex = resolvedMediaId
        ? attachments.findIndex((att) => getMediaIdFromUrl(att.link) === resolvedMediaId)
        : -1;
      if (sameMediaIndex >= 0) {
        attachments[sameMediaIndex] = {
          ...attachments[sameMediaIndex],
          link: resolvedAttachmentLink,
        };
      } else if (!attachments.some((att) => att.link === resolvedAttachmentLink)) {
        attachments.push({
          link: resolvedAttachmentLink,
          filename: singleAttachmentFilename,
          caption: singleAttachmentCaption,
        });
      }
    }
    // Keep media message identity stable across duplicate ws envelopes (e.g. partial then with URL).
    const incomingMessageId = isMediaPayloadType
      ? (mediaMessageId || msg.uuid || resolvedAttachmentLink)
      : (msg.uuid || mediaMessageId);

    const messageText =
      rawPayload?.text ||
      rawPayload?.message ||
      rawPayload?.caption ||
      singleAttachmentFilename ||
      (msg as any)?.text ||
      (msg as any)?.message ||
      '';

    callbacks.onIncomingMessage?.({
      channel: this.channel,
      senderId: String(msg.accountId),
      clientId: String(msg.targetId),
      msgId: incomingMessageId,
      message: messageText,
      date: dateTimeService.formatTimestamp(timestamp, this.config.dateFormat),
      status: ChatMessageStatus.UNREAD,
      outgoing: false,
      payloadType,
      attachments,
      button: rawPayload?.button,
      statusCode: 200,
      error: null,
    });
  }

  private onChatOutgoingMessage(wsMessage: ChatProxyWsOutgoingMessage, callbacks: ChatListenerCallbacks): void {
    const { payload } = wsMessage;
    const payloadType = this.normalizePayloadType(payload.payloadType);

    let text: string | undefined;
    const attachments: ChatAttachment[] = [];
    if (payload.attachmentLink) {
      text = undefined;
      attachments.push({
        link: payload.attachmentLink,
        filename: payload.fileName,
        caption: payload.message,
      });
    } else {
      text = payload.message;
    }

    callbacks.onOutgoingMessage?.({
      senderId: String(payload.accountId),
      clientId: String(payload.targetId),
      msgId: payload.messageId,
      message: text,
      date: dateTimeService.formatCurrentTimestamp(this.config.dateFormat),
      outgoing: true,
      channel: this.channel,
      status: ChatMessageStatus.QUEUED,
      payloadType,
      attachments,
      button: payload.button,
      statusCode: 200,
      error: null,
    });
  }

  private normalizePayloadType(payloadType: unknown): ChatMessagePayloadType {
    const value = String(payloadType || '').toLowerCase();
    switch (value) {
      case ChatMessagePayloadType.IMAGE:
        return ChatMessagePayloadType.IMAGE;
      case ChatMessagePayloadType.VIDEO:
        return ChatMessagePayloadType.VIDEO;
      case ChatMessagePayloadType.DOC:
        return ChatMessagePayloadType.DOC;
      case ChatMessagePayloadType.TEXT_BUTTON:
        return ChatMessagePayloadType.TEXT_BUTTON;
      case ChatMessagePayloadType.IMAGE_BUTTON:
        return ChatMessagePayloadType.IMAGE_BUTTON;
      case ChatMessagePayloadType.BUTTON:
        return ChatMessagePayloadType.BUTTON;
      case ChatMessagePayloadType.TEXT:
      default:
        return ChatMessagePayloadType.TEXT;
    }
  }

  private onChatTargetCreated(wsMessage: ChatProxyTargetCreated, callbacks: ChatListenerCallbacks): void {
    const { payload } = wsMessage;
    const isNumericDestination = phoneNumberService.isValidPhoneNumber(payload.destination || '');

    callbacks.onClientCreated?.({
      channel: this.channel,
      id: String(payload.targetId),
      type: isNumericDestination ? ChatClientType.NUMERIC : ChatClientType.ALPHANUMERIC,
      number: isNumericDestination
        ? phoneNumberService.toPhoneNumberWithPlusSign(payload.destination)
        : String(payload.destination || ''),
      lastActivityTime: payload.timestamp,
      senderId: String(payload.accountId),
      foundInContacts: false,
    });
  }
}
